import WhatsappCallsAPI from '../api/whatsappCalls';

const DEFAULT_ICE_SERVERS = [{ urls: 'stun:stun.l.google.com:19302' }];
const ICE_GATHER_TIMEOUT_MS = 10000;
const RECORDING_TIMESLICE_MS = 1000;
const RECORDER_MIME_CANDIDATES = [
  'audio/webm;codecs=opus',
  'audio/webm',
  'audio/ogg;codecs=opus',
];

let pc = null;
let localStream = null;
let remoteStream = null;
let remoteAudioEl = null;
let mediaRecorder = null;
let recorderChunks = [];
let audioContext = null;
let activeAccountId = null;
let activeCallId = null;
let activeCallSid = null;
let recordingEnabled = false;
let recorderArmed = false;

const ensureRemoteAudioElement = () => {
  if (remoteAudioEl) return remoteAudioEl;
  remoteAudioEl = document.createElement('audio');
  remoteAudioEl.id = 'whatsapp-call-remote-audio';
  remoteAudioEl.autoplay = true;
  remoteAudioEl.playsInline = true;
  remoteAudioEl.style.display = 'none';
  document.body.appendChild(remoteAudioEl);
  return remoteAudioEl;
};

const playRemoteStream = stream => {
  const el = ensureRemoteAudioElement();
  el.srcObject = stream;
  el.play().catch(() => {});
};

const setupRecorder = () => {
  if (!recordingEnabled || !localStream || !remoteStream || mediaRecorder) {
    return;
  }
  if (remoteStream.getAudioTracks().length === 0) {
    return;
  }

  // eslint-disable-next-line no-console
  console.log('[WhatsApp Call] Starting recorder');
  audioContext = new AudioContext({ sampleRate: 48000 });
  audioContext.resume().catch(() => {});

  const destination = audioContext.createMediaStreamDestination();
  audioContext.createMediaStreamSource(localStream).connect(destination);
  audioContext.createMediaStreamSource(remoteStream).connect(destination);

  const mimeType = RECORDER_MIME_CANDIDATES.find(t =>
    MediaRecorder.isTypeSupported(t)
  );
  if (!mimeType) {
    // eslint-disable-next-line no-console
    console.warn('[WhatsApp Call] No supported MIME type for recording');
    return;
  }

  recorderChunks = [];
  mediaRecorder = new MediaRecorder(destination.stream, {
    mimeType,
    audioBitsPerSecond: 48000,
  });
  mediaRecorder.ondataavailable = event => {
    if (event.data && event.data.size > 0) {
      recorderChunks.push(event.data);
      // eslint-disable-next-line no-console
      console.log('[WhatsApp Call] Recorder chunk', event.data.size);
    }
  };
  mediaRecorder.onstop = () => {
    // eslint-disable-next-line no-console
    console.log(
      '[WhatsApp Call] Recorder stopped, chunks:',
      recorderChunks.length
    );
  };
  mediaRecorder.start(RECORDING_TIMESLICE_MS);
};

const stopRecorderAndUpload = async (
  accountId,
  callId,
  chunks,
  recorder,
  ctx
) => {
  if (recorder && recorder.state !== 'inactive') {
    await new Promise(resolve => {
      recorder.addEventListener('stop', resolve, { once: true });
      try {
        recorder.stop();
      } catch (_) {
        resolve();
      }
    });
  }
  if (ctx && ctx.state !== 'closed') {
    ctx.close().catch(() => {});
  }
  if (!chunks.length || !callId || !accountId) {
    // eslint-disable-next-line no-console
    console.warn('[WhatsApp Call] Recording upload skipped', {
      chunks: chunks.length,
      callId,
      accountId,
    });
    return;
  }

  const blob = new Blob(chunks, { type: chunks[0].type });
  const filename = blob.type.startsWith('audio/webm')
    ? 'call-recording.webm'
    : 'call-recording.ogg';
  try {
    const response = await WhatsappCallsAPI.uploadRecording(
      accountId,
      callId,
      blob,
      filename
    );
    // eslint-disable-next-line no-console
    console.log('[WhatsApp Call] Recording uploaded', response);
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('[WhatsApp Call] Upload recording failed:', error);
  }
};

const buildPeerConnection = iceServers => {
  const config = iceServers?.length
    ? { iceServers }
    : { iceServers: DEFAULT_ICE_SERVERS };
  pc = new RTCPeerConnection(config);
  remoteStream = new MediaStream();
  pc.ontrack = event => {
    const tracks = event.streams?.[0]?.getTracks() || [event.track];
    tracks.forEach(track => {
      if (!remoteStream.getTracks().includes(track))
        remoteStream.addTrack(track);
    });
    playRemoteStream(remoteStream);
    if (recorderArmed) setupRecorder();
  };
  return pc;
};

const waitForIceGatheringComplete = peer =>
  new Promise(resolve => {
    if (peer.iceGatheringState === 'complete') {
      resolve();
      return;
    }
    const timer = setTimeout(resolve, ICE_GATHER_TIMEOUT_MS);
    peer.addEventListener('icegatheringstatechange', () => {
      if (peer.iceGatheringState === 'complete') {
        clearTimeout(timer);
        resolve();
      }
    });
  });

const cleanup = ({ callSid = null, upload = true } = {}) => {
  if (callSid && activeCallSid && callSid !== activeCallSid) return;

  const currentAccountId = activeAccountId;
  const currentCallId = activeCallId;
  const currentChunks = [...recorderChunks];
  const currentRecorder = mediaRecorder;
  const currentAudioContext = audioContext;

  if (upload) {
    stopRecorderAndUpload(
      currentAccountId,
      currentCallId,
      currentChunks,
      currentRecorder,
      currentAudioContext
    );
  }

  if (mediaRecorder && mediaRecorder.state !== 'inactive') {
    try {
      mediaRecorder.stop();
    } catch (_) {
      // ignore
    }
  }
  if (audioContext && audioContext.state !== 'closed') {
    audioContext.close().catch(() => {});
  }
  if (localStream) localStream.getTracks().forEach(t => t.stop());
  if (remoteStream) remoteStream.getTracks().forEach(t => t.stop());
  if (pc) pc.close();
  if (remoteAudioEl) remoteAudioEl.srcObject = null;

  pc = null;
  localStream = null;
  remoteStream = null;
  mediaRecorder = null;
  audioContext = null;
  activeAccountId = null;
  activeCallId = null;
  activeCallSid = null;
  recordingEnabled = false;
  recorderArmed = false;
  recorderChunks = [];
};

const applySdpAnswer = async sdpAnswer => {
  if (!pc || pc.currentRemoteDescription) return;
  await pc.setRemoteDescription({ type: 'answer', sdp: sdpAnswer });
};

export default {
  isActiveCall(callSid) {
    return !activeCallSid || !callSid || activeCallSid === callSid;
  },

  async initiateOutboundCall({
    accountId,
    conversationId,
    recordingEnabled: shouldRecord = false,
    useSip = false,
  }) {
    if (useSip) {
      throw new Error(
        'SIP media is not implemented in the browser yet. Disable use_sip in the inbox settings to use WebRTC.'
      );
    }
    if (pc) {
      throw new Error('Another WhatsApp call session is active');
    }
    cleanup({ upload: false });
    activeAccountId = accountId;
    recordingEnabled = shouldRecord;
    recorderArmed = shouldRecord;
    localStream = await navigator.mediaDevices.getUserMedia({ audio: true });
    buildPeerConnection();
    localStream.getTracks().forEach(track => pc.addTrack(track, localStream));

    const offer = await pc.createOffer();
    await pc.setLocalDescription(offer);
    await waitForIceGatheringComplete(pc);

    const response = await WhatsappCallsAPI.initiate(
      { accountId, conversationId },
      pc.localDescription.sdp
    );
    activeCallId = response.id;
    activeCallSid = response.provider_call_id;
    recordingEnabled = response.recording_enabled !== false;
    recorderArmed = recordingEnabled;
    if (remoteStream?.getAudioTracks().length) setupRecorder();
    // eslint-disable-next-line no-console
    console.log('[WhatsApp Call] Outbound session ready', {
      callId: activeCallId,
      callSid: activeCallSid,
      recordingEnabled,
    });
    return response;
  },

  async acceptIncomingCall({
    accountId,
    callId,
    callSid,
    sdpOffer,
    iceServers,
    recordingEnabled: shouldRecord = false,
  }) {
    cleanup({ upload: false });
    activeAccountId = accountId;
    recordingEnabled = shouldRecord;
    recorderArmed = true;
    activeCallId = callId;
    activeCallSid = callSid;
    localStream = await navigator.mediaDevices.getUserMedia({ audio: true });
    buildPeerConnection(iceServers || DEFAULT_ICE_SERVERS);
    localStream.getTracks().forEach(track => pc.addTrack(track, localStream));

    await pc.setRemoteDescription({ type: 'offer', sdp: sdpOffer });
    const answer = await pc.createAnswer();
    await pc.setLocalDescription(answer);
    await waitForIceGatheringComplete(pc);

    await WhatsappCallsAPI.accept(accountId, callId, pc.localDescription.sdp);
  },

  async endActiveCall(callId = activeCallId) {
    const callSid = activeCallSid;
    if (callId && activeAccountId) {
      try {
        await WhatsappCallsAPI.terminate(activeAccountId, callId);
      } catch (_) {
        // ignore
      }
    }
    cleanup({ callSid });
  },

  async rejectIncomingCall(accountId, callId) {
    try {
      await WhatsappCallsAPI.reject(accountId, callId);
    } catch (_) {
      // ignore
    }
    cleanup();
  },

  applySdpAnswer,
  cleanup,

  get activeCallId() {
    return activeCallId;
  },
  get activeCallSid() {
    return activeCallSid;
  },
  get isCallActive() {
    return !!pc;
  },
  get localStream() {
    return localStream;
  },
};
